/**
 * Conflict detection utilities for calendar events.
 * Shared logic for detecting schedule conflicts.
 */

import { hoursForDate, weekdayIndexOf } from '../../../utils/weekdayHours';
import { parseTimeToHour } from './dateUtils';

/**
 * Check if two time ranges overlap.
 * @param {Date} start1 - Start of first range
 * @param {Date} end1 - End of first range
 * @param {Date} start2 - Start of second range
 * @param {Date} end2 - End of second range
 * @returns {boolean} True if ranges overlap
 */
export function timesOverlap(start1, end1, start2, end2) {
  return start1 < end2 && end1 > start2;
}

/**
 * Format time for conflict messages (HH:mm format).
 * @param {Date} date - Date to format
 * @returns {string} Formatted time string
 */
export function formatTimeForConflict(date) {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Detect conflicts between a planned event and existing events/unavailabilities.
 *
 * @param {Object} params - Detection parameters
 * @param {Date} params.plannedStart - Start time of planned event
 * @param {Date} params.plannedEnd - End time of planned event
 * @param {number[]} params.employeeIds - Employee IDs for the planned event
 * @param {number|null} params.serviceBayId - Service bay ID for the planned event
 * @param {Object[]} params.existingEvents - Array of existing events to check against
 * @param {Object[]} params.unavailabilities - Array of employee unavailabilities to check against
 * @param {number|null} params.excludeEventId - Event ID to exclude (for edit mode)
 * @param {Object[]} params.employees - Employee lookup array (for names)
 * @param {Object[]} params.serviceBays - Service bay lookup array (for names)
 * @param {Function} params.t - Translation function
 * @returns {Object[]} Array of conflict objects { type, message }
 */
export function detectConflicts({
  plannedStart,
  plannedEnd,
  employeeIds = [],
  serviceBayId = null,
  existingEvents = [],
  unavailabilities = [],
  excludeEventId = null,
  employees = [],
  serviceBays = [],
  t,
}) {
  const conflicts = [];

  // Check for scheduling outside employee work hours
  employeeIds.forEach((empId) => {
    const emp = employees.find((e) => e.id === empId);
    if (!emp) return;

    // That weekday's shift (work_hours, per weekday); a calendar response from before per-weekday
    // hours only has one period and the working weekdays.
    let shift;
    if (emp.work_hours) {
      shift = hoursForDate(emp.work_hours, plannedStart);
    } else {
      if (!emp.work_day_start || !emp.work_day_end) return;
      const workingDays = emp.working_weekdays || [];
      const onWorkingDay = workingDays.length === 0 || workingDays.includes(weekdayIndexOf(plannedStart));
      shift = onWorkingDay ? { start: emp.work_day_start, end: emp.work_day_end } : null;
    }

    if (!shift) {
      conflicts.push({
        type: 'work_hours',
        message: t('notWorkingDay', { name: emp.name }),
      });
      return;
    }

    const workStart = parseTimeToHour(shift.start);
    const workEnd = parseTimeToHour(shift.end);
    const eventStart = plannedStart.getHours() + plannedStart.getMinutes() / 60;
    const eventEnd = plannedEnd.getHours() + plannedEnd.getMinutes() / 60;

    if (eventStart < workStart || eventEnd > workEnd) {
      conflicts.push({
        type: 'work_hours',
        message: t('outsideWorkHours', { name: emp.name, start: shift.start, end: shift.end }),
      });
    }
  });

  // Check for employee unavailability conflicts
  unavailabilities.forEach((unavail) => {
    // Only check if this unavailability is for one of the selected employees
    if (!employeeIds.includes(unavail.employee_id)) {
      return;
    }

    const unavailStart = new Date(unavail.start_time);
    const unavailEnd = new Date(unavail.end_time);

    // Check if times overlap
    if (!timesOverlap(plannedStart, plannedEnd, unavailStart, unavailEnd)) {
      return;
    }

    const employeeName = employees.find((e) => e.id === unavail.employee_id)?.name || '';
    // Use title if available, otherwise translate the type
    const unavailType = unavail.title || t(`unavailability_type_${unavail.type}`) || t('Unavailable');
    conflicts.push({
      type: 'unavailability',
      message: t('employeeNotAvailable', { name: employeeName, reason: unavailType }),
    });
  });

  // Check for maintenance event conflicts
  existingEvents.forEach((existingEvent) => {
    // Skip the event being edited/moved
    if (excludeEventId && existingEvent.id === excludeEventId) {
      return;
    }

    const existingStart = new Date(existingEvent.start_time);
    const existingEnd = new Date(existingEvent.end_time);

    // Check if times overlap
    if (!timesOverlap(plannedStart, plannedEnd, existingStart, existingEnd)) {
      return;
    }

    // Check for employee conflicts
    const existingEmployeeIds = existingEvent.employee_ids || [];
    const conflictingEmployees = employeeIds.filter((empId) =>
      existingEmployeeIds.includes(empId)
    );

    if (conflictingEmployees.length > 0) {
      const employeeNames = conflictingEmployees
        .map((empId) => employees.find((e) => e.id === empId)?.name || empId)
        .join(', ');
      const timeStr = `${formatTimeForConflict(existingStart)}-${formatTimeForConflict(existingEnd)}`;
      conflicts.push({
        type: 'employee',
        message: `${t('Employee')} ${employeeNames} ${t('is already scheduled')}: ${existingEvent.vehicle_registration || ''} (${timeStr})`,
      });
    }

    // Check for service bay conflicts
    if (serviceBayId && existingEvent.service_bay_id === serviceBayId) {
      const bayName = serviceBays.find((b) => b.id === serviceBayId)?.name || serviceBayId;
      const timeStr = `${formatTimeForConflict(existingStart)}-${formatTimeForConflict(existingEnd)}`;
      conflicts.push({
        type: 'serviceBay',
        message: `${t('Service bay')} ${bayName} ${t('is already booked')}: ${existingEvent.vehicle_registration || ''} (${timeStr})`,
      });
    }
  });

  return conflicts;
}
